import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import LoginSideImg from '../../assets/SVGs/LogInSideImage';
import LoadingOverlay from '../insurance/LoadingOverlay';
import { resetPasswordApi } from '../../utils/apis';

const PASSWORD_HINT =
  'At least 8 characters, with an uppercase letter, a lowercase letter, a number, and a special character.';

/**
 * Token+link reset step. The emailed link lands here as /reset-password?token=...
 * The user sets a new password, which is confirmed via POST /auth/reset-password
 * ({ token, newPassword }). Errors surface the backend's message
 * (error.response.data.message) — token expired/used/not found, password policy.
 */
const ResetPassword: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Capture the single-use token ONCE into state, then scrub it from the address bar so it doesn't
  // linger in history / referrer for the life of the page. The form works off the captured value, so
  // removing it from the URL can't blank the page.
  const [token] = useState(() => searchParams.get('token') || '');
  useEffect(() => {
    if (token && typeof window !== 'undefined' && window.history?.replaceState) {
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, [token]);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const inputClass =
    'w-full bg-white text-[#29406B] border-1 font-medium px-4 py-3 rounded-[12px] ring-1 ring-inset ring-[#DCE1E8]';
  const labelClass = 'font-semibold text-base text-[#29406B]';

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPassword) return;
    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'The passwords do not match.' });
      return;
    }
    setLoading(true);
    setMessage(null);
    try {
      await resetPasswordApi({ token, newPassword });
      setMessage({ type: 'success', text: 'Your password has been reset. Redirecting to sign in…' });
      setTimeout(() => navigate('/login'), 1800);
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.response?.data?.message || "We couldn't reset your password. Please request a new link and try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col overflow-hidden">
      <div className="flex flex-1">
        <div className="flex flex-col justify-start md:justify-center w-full md:w-[55%] bg-[#F2F5F9] p-6 lg:p-12 sm:mt-0">
          <div className="sm:px-[32px] md:px-[64px]">
            <h2 className="mt-10 text-left text-2xl font-semibold leading-9 text-[#29406B] sm:mt-0">
              Reset your password
            </h2>
            <p className="mt-2 text-sm text-[#5b6b8c]">
              Choose a new password to finish resetting your account.
            </p>
          </div>

          <div className="mt-6 sm:px-[32px] md:px-[64px]">
            {loading && <LoadingOverlay />}
            {message && (
              <div
                className={`p-4 rounded-md mb-4 ${
                  message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}
              >
                {message.text}
              </div>
            )}
          </div>

          <div className="mt-4 sm:px-[32px] md:px-[64px] sm:mt-0">
            {!token ? (
              <div className="p-4 rounded-md mb-4 bg-red-100 text-red-700">
                This reset link is invalid or incomplete. Please request a new password reset link.
              </div>
            ) : (
              <form className="space-y-6" onSubmit={handleReset}>
                <div>
                  <label htmlFor="rp-new" className={labelClass}>New password</label>
                  <div className="mt-2">
                    <input
                      id="rp-new"
                      type="password"
                      value={newPassword}
                      required
                      onChange={(e) => { setNewPassword(e.target.value); setMessage(null); }}
                      className={inputClass}
                    />
                  </div>
                  <p className="mt-1 text-xs text-[#5b6b8c]">{PASSWORD_HINT}</p>
                </div>
                <div>
                  <label htmlFor="rp-confirm" className={labelClass}>Confirm new password</label>
                  <div className="mt-2">
                    <input
                      id="rp-confirm"
                      type="password"
                      value={confirmPassword}
                      required
                      onChange={(e) => { setConfirmPassword(e.target.value); setMessage(null); }}
                      className={inputClass}
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className={`flex px-4 py-3 w-full justify-center rounded-md text-xl font-semibold text-white shadow-sm disabled:opacity-60 ${theme['button-bg']}`}
                >
                  Reset password
                </button>
              </form>
            )}

            <p className="mt-8 text-center text-base font-semibold">
              <Link to="/login" className={`text-${theme.color} underline cursor-pointer`}>Back to sign in</Link>
            </p>
          </div>
        </div>

        <div className="hidden md:flex md:w-[45%] items-center justify-center bg-[#242E49] h-screen">
          <LoginSideImg />
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
